import React from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

interface MktoForms2Form {
  showErrorMessage: (msg: string, elem?: any) => object;
  submit: () => object;
  submittable: (canSubmit?: boolean) => boolean | object;
  onSubmit: (callback?: () => void) => object;
  onSuccess: (
    callback?: (values: object, followUpUrl: string) => boolean,
  ) => object;
  onValidate: (callback?: (builtInValidation: boolean) => void) => object;
}

interface MktoForms2 {
  loadForm: (
    baseUrl: string,
    munchkinId: string,
    formId: string | number,
    callback?: (form: MktoForms2Form) => void,
  ) => void;
  whenReady: (callback?: (form: MktoForms2Form) => void) => void;
}

type MarketoWindow = typeof window & { MktoForms2: MktoForms2 };

export interface MarketoFormProps {
  className?: string;
  baseUrl?: string;
  munchkinId?: string;
  formId?: string | number;
  formName?: string;
}

export const MarketoForm: React.FC<MarketoFormProps> = ({
  className,
  baseUrl,
  munchkinId,
  formId,
  formName,
}) => {
  const [scriptLoaded, setScriptLoaded] = React.useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleReCaptchaVerify = React.useCallback(async () => {
    if (!executeRecaptcha) {
      return Promise.reject(new Error('Site key is undefined'));
    }

    try {
      const token = await executeRecaptcha(formName);
      const response = await fetch('/api/recaptcha-siteverify', {
        method: 'POST',
        body: JSON.stringify({
          grecaptchaToken: token,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const result: { success: boolean } = await response.json();
      if (!result.success) {
        return Promise.reject(new Error('May be a robot'));
      }
    } catch (error) {
      return Promise.reject(
        new Error(
          `Probably, this domain is not supported for this site key. ${error}`,
        ),
      );
    }

    return Promise.resolve();
  }, [executeRecaptcha, formName]);

  React.useEffect(() => {
    if (!(baseUrl && munchkinId && formId)) {
      return;
    }

    const appendScript = () => {
      const script = document.createElement('script');
      script.src = `${baseUrl}/js/forms2/js/forms2.min.js`;
      script.onload = () => {
        if ('MktoForms2' in window) {
          setScriptLoaded(true);
        } else {
          setScriptLoaded(false);
        }
      };
      document.body.appendChild(script);
      return script;
    };

    const targetForm = document.getElementById(`mktoForm_${formId}`);
    if (scriptLoaded) {
      if (targetForm != null) {
        targetForm.innerHTML = '';
      }

      (window as MarketoWindow).MktoForms2.loadForm(
        baseUrl,
        munchkinId,
        formId,
      );

      (window as MarketoWindow).MktoForms2.whenReady((form) => {
        form.submittable(false);

        /**
         * Probably, submit event and validate event are called in parallel,
         * so submit will fail if submittable is false eventhough it becomes true after validation.
         *
         * Moreover, onValidate will run when user click submit button, call submit() function, and call validate() function, and
         * we don't use validate() function for now.
         *
         * Thereby, this function has submit() function as the last process so that we can continue the submit process
         * when the validation successes.
         *
         */
        form.onValidate(async (builtInValidation) => {
          if (form.submittable()) {
            return form;
          }

          if (!builtInValidation) {
            return form;
          }

          try {
            await handleReCaptchaVerify();
          } catch {
            form.submittable(false);
            form.showErrorMessage(
              '送信エラー: 申し訳ありません、時間をおいて再度お試しください',
            );
            return form;
          }

          form.submittable(true);
          form.submit();
          return form;
        });

        form.onSuccess((values, followUpUrl) => {
          window.parent.location.href = followUpUrl;
          return false;
        });
      });

      return;
    }
    const script = appendScript();
    return () => {
      if (targetForm != null) {
        targetForm.innerHTML = '';
      }
      script?.remove();
    };
  }, [scriptLoaded, handleReCaptchaVerify, baseUrl, munchkinId, formId]);

  return <form id={`mktoForm_${formId}`} className={className}></form>;
};
